import { Injectable } from '@angular/core';
import { StatsigClient } from '@statsig/js-client';
import { environment } from 'shared/environments/environment';

//NOTE: this is used for our AB tests

// Each running test: the parameter its cell is read from and the cells the funnel can render.
// The first cell is the control, which a missing or unrecognised value falls back to.
const EXPERIMENTS = {
  funnel_first_screen_nov_2026: { param: 'screen', cells: ['age', 'readingToAudio'] },
  kindle_second_screen_nov_2026: { param: 'screen', cells: ['gender', 'age'] },
  kindle_no_books_flow_nov_2026: { param: 'flow', cells: ['kindle', 'main'] },
} as const;

export type ExperimentName = keyof typeof EXPERIMENTS;

export type ExperimentCell<N extends ExperimentName> = (typeof EXPERIMENTS)[N]['cells'][number];

@Injectable({
  providedIn: 'root',
})
export class StatsIGService {
  private client: StatsigClient;
  private readonly loggedExposures = new Set<ExperimentName>();

  constructor() {
    this.client = new StatsigClient(
      'client-T5698fFDyjPAgSGO1ROSaKP1nwzaAHEQRDSyWScLlQg',
      {
        appVersion: environment.appVersion,
      },
      {
        environment: {
          tier: environment.production ? 'production' : 'development',
        },
      }
    );
  }

  async initializeStatsig() {
    await this.client.initializeAsync();
  }

  /**
   * The visitor's cell, validated so a typo in the Statsig setup falls back to the control
   * instead of a value the funnel cannot render. With `logExposure: false` it only peeks: a test
   * whose screen comes later must count as exposed only the visitors who reach it - see
   * logExposure().
   */
  getCell<N extends ExperimentName>(
    name: N,
    { logExposure = true }: { logExposure?: boolean } = {}
  ): ExperimentCell<N> {
    const { param, cells } = EXPERIMENTS[name];
    const options = logExposure ? undefined : { disableExposureLog: true };
    const value = this.client.getExperiment(name, options).get(param, cells[0]);

    if ((cells as readonly unknown[]).includes(value)) {
      return value as ExperimentCell<N>;
    }

    console.warn(`Unrecognised ${name} cell from Statsig, falling back to ${cells[0]}`, value);

    return cells[0];
  }

  /**
   * Logs the exposure a peeked getCell() skipped, once per page load however often it is called.
   * The SDK memoises evaluations per options, so this plain read is a fresh evaluation that logs.
   */
  logExposure(name: ExperimentName): void {
    if (this.loggedExposures.has(name)) {
      return;
    }

    this.loggedExposures.add(name);
    this.client.getExperiment(name);
  }
}
